/**
 * Lightweight client-error telemetry.
 *
 * Pipes `console.error`, `console.warn`, `window.onerror`, and
 * `unhandledrejection` into `/api/telemetry/client-errors`. Rate-limited to
 * 10 events per sliding 60-second window per tab session to avoid flooding
 * the DB when a render loop explodes.
 *
 * Active in both dev and production by default — the original console
 * behaviour is preserved (we do NOT swallow errors), we simply mirror
 * them to the backend. In production the DevTools lockdown silences
 * `console.log/info/debug/trace` but deliberately keeps `error/warn`
 * so the ops signal still reaches this telemetry pipe.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API}/api/telemetry/client-errors`;
const SESSION_KEY = 'aerosky:telemetry-session';
const MAX_EVENTS_PER_MIN = 10;

let installed = false;
const recentTimestamps = [];

function sessionId() {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return 'anon';
  }
}

function withinRateLimit() {
  const now = Date.now();
  while (recentTimestamps.length && now - recentTimestamps[0] > 60_000) {
    recentTimestamps.shift();
  }
  if (recentTimestamps.length >= MAX_EVENTS_PER_MIN) return false;
  recentTimestamps.push(now);
  return true;
}

function report(level, { message, stack, source, lineNo, columnNo, extra } = {}) {
  if (!withinRateLimit()) return;
  const body = JSON.stringify({
    level,
    message: String(message ?? ''),
    stack: stack ? String(stack) : null,
    source: source ? String(source) : null,
    line_no: typeof lineNo === 'number' ? lineNo : null,
    column_no: typeof columnNo === 'number' ? columnNo : null,
    url: window.location.href,
    user_agent: navigator.userAgent,
    session_id: sessionId(),
    app_version: process.env.REACT_APP_VERSION || null,
    extra: extra || null,
  });
  // Prefer sendBeacon when available (survives page unload), fall back to fetch.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch {
    /* fall through */
  }
  try {
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      credentials: 'include',
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* silent — telemetry must never throw */
  }
}

function stringifyArg(a) {
  if (a instanceof Error) return `${a.name}: ${a.message}`;
  if (typeof a === 'object') {
    try { return JSON.stringify(a); } catch { return String(a); }
  }
  return String(a);
}

function firstStack(args) {
  for (const a of args) {
    if (a instanceof Error && a.stack) return a.stack;
  }
  return null;
}

export default function installTelemetry() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // Mirror console.error
  const origError = window.console.error.bind(window.console);
  window.console.error = (...args) => {
    try {
      report('error', {
        message: args.map(stringifyArg).join(' '),
        stack: firstStack(args),
      });
    } catch { /* ignore */ }
    origError(...args);
  };

  // Mirror console.warn
  const origWarn = window.console.warn.bind(window.console);
  window.console.warn = (...args) => {
    try {
      report('warn', {
        message: args.map(stringifyArg).join(' '),
        stack: firstStack(args),
      });
    } catch { /* ignore */ }
    origWarn(...args);
  };

  // Uncaught runtime errors
  window.addEventListener('error', (e) => {
    report('window_error', {
      message: e.message || 'Uncaught error',
      stack: e.error && e.error.stack ? e.error.stack : null,
      source: e.filename || null,
      lineNo: e.lineno,
      columnNo: e.colno,
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason;
    report('unhandled_rejection', {
      message: reason instanceof Error ? `${reason.name}: ${reason.message}` : stringifyArg(reason),
      stack: reason instanceof Error ? reason.stack : null,
    });
  });
}
